import { PALETTE, VISUAL_ROLES, type CreatureTone, type VisualRole } from "@eldrun/content";

/**
 * Superfície dos atores animados (Fase 16, F118; ADR 0025): o PBR com a cor original do modelo (ou o
 * vidro, no herói), a máscara da roupa, a costura e o Fresnel na cor do acento. Tudo é uniform (mudar
 * não recompila nem reconstrói). Fase 15: facetas limpas, sem as linhas dos triângulos nem os nós.
 */
export interface CharacterSurfaceStyle {
  /**
   * Costura, Fresnel e a luz do vidro ligados. Desligado, sobra o PBR com a cor original e a máscara na
   * cor da classe (linguagem de gameplay, ADR 0017: não é efeito).
   */
  enabled: boolean;
  /**
   * Cor do acento (hex sRGB): a da classe, a do tom da criatura. Acende os acentos e tinge a máscara da
   * roupa (`tint`, onde houver: a cor da classe dos heróis); o resto do albedo não muda.
   */
  accentColor: string;
  /** Costura acesa (máscara `tint`) na cor do acento: o envelope do golpe dos heróis. */
  seamIntensity: number;
  /** Fresnel na cor do acento (o rim do papel; a ameaça das criaturas o aumenta). */
  fresnel: number;
  /**
   * Vidro (Fase 13): 0 deixa a cor do modelo; 1 vira vidro fosco na luminância do modelo, tingido na cor
   * do acento (metade no corpo, quase toda na máscara da roupa, Fase 15), menos rugoso, com o Fresnel e
   * um brilho interno na cor do acento.
   */
  glass: number;
}

/** Faixas seguras (a costura e o Fresnel dos papéis de hoje, o vidro). */
export const CHARACTER_SURFACE_RANGES = {
  seamIntensity: [0, 1],
  fresnel: [0, 1.5],
  glass: [0, 1],
} as const satisfies Partial<Record<keyof CharacterSurfaceStyle, readonly [number, number]>>;

/** Valores iniciais: a cor original, sem acento (os papéis ligam o deles). */
export const CHARACTER_SURFACE_DEFAULTS: Readonly<CharacterSurfaceStyle> = {
  enabled: true,
  accentColor: PALETTE.classes.lanceiro.hex,
  seamIntensity: 0,
  fresnel: 0,
  glass: 0,
};

/** Papéis visuais com corpo animado (os núcleos acesos, a arma e os FX ficam na malha neon). */
export type ActorRole = Extract<VisualRole, "heroi" | "inimigo" | "chefe" | "fauna">;

/**
 * O que muda por papel. A costura e o Fresnel de repouso e de pico continuam nos papéis visuais
 * (`VISUAL_ROLES`, a mesma linguagem de gameplay da malha neon); aqui só o vidro.
 */
export const ACTOR_PRESETS: Readonly<Record<ActorRole, Partial<Pick<CharacterSurfaceStyle, "glass">>>> = {
  // Fase 13: o herói é de vidro na cor da classe (Fase 15: sem as quinas, o vidro carrega a cor).
  heroi: { glass: 1 },
  // Monstros, chefes e fauna: a cor do modelo.
  inimigo: {},
  chefe: {},
  fauna: {},
};

/** O papel do corpo de uma criatura pelo tom (o mesmo de `toneRoles`, só os corpos). */
export function creatureRole(tone: CreatureTone): ActorRole {
  return tone === "chefe" ? "chefe" : tone === "inimigo" ? "inimigo" : "fauna";
}

/**
 * O estilo de um ator: o vidro do papel, a costura e o Fresnel de repouso do papel visual
 * (os de pico entram pelos setters: o envelope do golpe, a ameaça) e a cor do dono.
 */
export function actorStyle(role: ActorRole, accentColor: string): Partial<CharacterSurfaceStyle> {
  const r = VISUAL_ROLES[role];
  return { ...ACTOR_PRESETS[role], accentColor, seamIntensity: r.accent, fresnel: r.rim };
}

const clamp = (v: number, [lo, hi]: readonly [number, number]) => (Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : lo);

/** O estilo completo e dentro das faixas (entrada de URL, de painel ou de chamador). */
export function clampStyle(style: Partial<CharacterSurfaceStyle> = {}): CharacterSurfaceStyle {
  const s = { ...CHARACTER_SURFACE_DEFAULTS, ...style };
  const r = CHARACTER_SURFACE_RANGES;
  return {
    enabled: s.enabled,
    accentColor: s.accentColor,
    seamIntensity: clamp(s.seamIntensity, r.seamIntensity),
    fresnel: clamp(s.fresnel, r.fresnel),
    glass: clamp(s.glass, r.glass),
  };
}
